import {
  ButtonColor,
  ButtonSize,
  LoaderInformation,
  Modal,
  ModalType,
  Table,
  TableRow,
} from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ApplicationDocumentPageValidation,
  CardApplication,
  Document,
  ProductOperationsCardApplyUpdate,
} from 'src/front/api/apiInstance';

import {
  CardSelectionType,
  ReferenceDataType,
  ScoringResultType,
  UploadType,
} from 'src/front/api/generated';

import infoIcon from 'src/front/assets/icon_info.svg';
import usePRM from 'src/front/common/hooks/usePRM';

import { dictionaryLineFormat } from 'src/front/common/utils/formUtils';
import preview from 'src/front/common/utils/previewUtils';

import AsyncButton from 'src/front/components/AsyncButton';

import UploadDocumentModal, {
  UploadDocumentFormData,
} from 'src/front/components/document/UploadDocumentModal';

import {
  Container,
  FormRow,
  Separator,
  Subtitle,
  TableContainer,
  TableWrapper,
  Title,
} from 'src/front/components/FormStyles';
import { useCardTypeState, useProductOfferDebitState } from 'src/front/contexts/CardEntryContext';
import styled from 'styled-components';

const WarningContainer = styled.div`
  display: flex;
`;

const WarningIcon = styled.img`
  padding-right: 5px;
`;

export const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const documentTableHeaders = [
  'general_documentType_label',
  'general_note_label',
  'general_uploadedDate_text',
];

interface Props {
  goToSuccessStep: (successMesage: string, status?: ScoringResultType) => void;
  customerId?: string;
  isEdit?: boolean;
  goToFirstStep?: () => void;
}

const ApplicationDocumentModal: FC<Props> = ({
  customerId,
  goToSuccessStep,
  goToFirstStep,
  isEdit = false,
}) => {
  const { t } = useTranslation();
  const cardType = useCardTypeState();
  const isProductOfferDebit = useProductOfferDebitState();
  const documentTypes = usePRM(ReferenceDataType.CREDITCARDDOCUMENTTYPE);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDocumentModal, setShowDocumentModal] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [executedOperationCompletedMessage, setExecutedOperationCompletedMessage] = useState<
    string
  >();

  const [selectedDocument, setSelectedDocument] = useState<TableRow>();
  const [documentFormValues, setDocumentFormValues] = useState<UploadDocumentFormData>();
  const [documents, setDocuments] = useState<UploadDocumentFormData[]>([]);
  const documentsRef = useRef<UploadDocumentFormData[]>(documents);

  const loadDocuments = useCallback(async (): Promise<void> => {
    try {
      const { data } = await ApplicationDocumentPageValidation.getApplicationDocumentPageData();
      if (data.result === 'OK' && data.pageData && data.pageData.documentUploadDataList) {
        const uploadedDocumentList =
          data.pageData.documentUploadDataList.map((document) => ({
            documentType: documentTypes.find(
              (type) => type.value === document.documentType.code
            ) || {
              value: document.documentType.code,
              label: document.documentType.code,
            },
            explanation: document.explanation || '',
            isOldDocument: document.isOldDocument,
            documentId: document.documentId,
            createDate: document.createDate,
            documentName: document.documentName,
          })) || [];
        setDocuments(uploadedDocumentList);
      }
    } catch (error) {
      // TODO (mertohancan): show error on modal or handle with error boundary
    }
  }, [documentTypes]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  useEffect(() => {
    return () => {
      ApplicationDocumentPageValidation.validateApplicationDocumentPageData({
        documentUploadDataList: documentsRef.current.map((document) => ({
          ...document,
          documentType: dictionaryLineFormat(document.documentType),
        })),
      });
    };
  }, []);

  const handleSubmit = async (): Promise<void> => {
    try {
      setIsLoading(true);
      if (isEdit) {
        const {
          data: response,
        } = await ProductOperationsCardApplyUpdate.updateCardApplyDataByReferenceNumber({
          documentList: documents.map((document) => ({
            ...document,
            documentType: dictionaryLineFormat(document.documentType),
          })),
        });
        if (response.result === 'OK') {
          setExecutedOperationCompletedMessage(response.applicationResultMessage);
        }
      } else {
        const { data: response } = await CardApplication.generateCardApplication({
          documentList: documents.map((document) => ({
            ...document,
            documentType: dictionaryLineFormat(document.documentType),
          })),
        });
        if (response.result === 'OK') {
          goToSuccessStep(response.applicationResultMessage, response.scoringResultType);
        }
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const closeDocumentModal = (): void => setShowDocumentModal(false);

  const handleDocumentSubmit = async (formData: UploadDocumentFormData): Promise<void> => {
    let newDocuments;
    if (!formData.documentId && formData.documentType) {
      const { data } = await Document.uploadDocuments(
        formData.files && formData.files[0],
        formData.documentType.value,
        UploadType.APPLICATION,
        formData.explanation,
        customerId
      );
      if (data.result === 'OK') {
        newDocuments = [
          {
            documentType: documentTypes.find((type) => type.value === data.documentType) || {
              value: data.documentType,
              label: data.documentType,
            },
            explanation: data.explanation,
            isOldDocument: data.isOldDocument,
            documentId: data.documentId,
            createDate: data.createDate,
            filePath: formData.filePath,
            documentName: data.documentName,
          },
          ...documents,
        ];
      }
    } else if (formData.documentType) {
      const { data } = await Document.updateDocument({
        documentId: formData.documentId,
        explanation: formData.explanation,
        documentType: formData.documentType.value,
        documentName: formData.documentName,
        customerId,
        uploadType: UploadType.APPLICATION,
      });
      if (data.result === 'OK') {
        newDocuments = documents.map((doc) =>
          doc.documentId === formData.documentId
            ? {
                documentType: documentTypes.find((type) => type.value === data.documentType) || {
                  value: data.documentType,
                  label: data.documentType,
                },
                explanation: data.explanation,
                isOldDocument: data.isOldDocument,
                documentId: data.documentId,
                createDate: data.createDate,
                filePath: formData.filePath,
                documentName: data.documentName,
              }
            : doc
        );
      }
    }
    setDocuments(newDocuments || []);
    setShowDocumentModal(false);
  };

  const handleDeleteDocument = async (): Promise<void> => {
    const { data } = await Document.deleteDocument({
      documentId: selectedDocument?.id,
      uploadType: UploadType.APPLICATION,
    });
    if (data.result === 'OK') {
      setDocuments(documents.filter((doc) => doc.documentId !== selectedDocument?.id));
      setSelectedDocument(undefined);
      setIsDeleteModalOpen(false);
    }
  };

  const handleExecutedOperationCompletedModal = (): void => {
    setExecutedOperationCompletedMessage(undefined);
    goToFirstStep && goToFirstStep();
  };

  const addDocument = useCallback((): void => {
    setDocumentFormValues(undefined);
    setShowDocumentModal(true);
  }, []);

  const editDocument = useCallback(
    (item?: TableRow): void => {
      setDocumentFormValues(documents.find((doc) => doc.documentId === item?.id));
      setShowDocumentModal(true);
    },
    [documents]
  );
  const previewDocument = useCallback(
    (item?: TableRow): void => {
      preview(documents, item);
    },
    [documents]
  );
  const deleteDocument = useCallback((item?: TableRow): void => {
    setIsDeleteModalOpen(true);
  }, []);

  const actions = useMemo(
    () => ({
      add: addDocument,
      edit: editDocument,
      delete: deleteDocument,
      preview: previewDocument,
    }),
    [addDocument, deleteDocument, editDocument, previewDocument]
  );

  const documentTableData: TableRow[] = documents.map(
    (document: UploadDocumentFormData): TableRow => ({
      id: document.documentId,
      columns: [document.documentType?.label || '', document.explanation, document.createDate],
      deletable: !document.isOldDocument,
      editable: !document.isOldDocument,
      previewable: !document.isOldDocument,
    })
  );

  return (
    <Container>
      {isLoading ? (
        <LoaderInformation information={t('general_transactionInProgress_errorMessage')} />
      ) : null}
      <Title>
        <Trans i18nKey="cardEntry_appDocumentUpload_title" />
      </Title>
      <FormRow>
        <Trans i18nKey="cardEntry_appDocumentUpload_text" />
      </FormRow>
      <WarningContainer>
        <WarningIcon src={infoIcon} />
        <Subtitle>
          <Trans
            i18nKey={
              !isProductOfferDebit && cardType === CardSelectionType.CREDITCARD
                ? 'cardEntry_appDocumentUpload_creditCard_text'
                : 'cardEntry_appDocumentUpload_debitCard_text'
            }
          />
        </Subtitle>
      </WarningContainer>
      {showDocumentModal ? (
        <UploadDocumentModal
          documentTypes={documentTypes}
          formValues={documentFormValues}
          onClose={closeDocumentModal}
          submit={handleDocumentSubmit}
        />
      ) : null}
      {isDeleteModalOpen ? (
        <Modal
          cancelText={t('general_cancel_button')}
          description={t('general_deleteRecordConfirm_text')}
          onCancel={() => setIsDeleteModalOpen((state) => !state)}
          onSubmit={handleDeleteDocument}
          submitText={t('general_components_confirm_button')}
          title="Confirmation"
          type={ModalType.Confirmation}
        />
      ) : null}

      {executedOperationCompletedMessage ? (
        <Modal
          description={executedOperationCompletedMessage}
          onCancel={handleExecutedOperationCompletedModal}
          submitText={t('general_components_close_button')}
          title={t('general_wellDone_text')}
          type={ModalType.Success}
        />
      ) : null}

      <Separator />
      <Subtitle>
        <Trans i18nKey="cardEntry_appDocumentUpload_uploadedDocuments_subtitle" />
      </Subtitle>
      <TableWrapper>
        <TableContainer>
          <Table
            actionConfig={actions}
            addActionText={t('general_add_button')}
            emptyTableText={t('cardEntry_documentEntry_noUploadedDocumentFound_text')}
            headers={documentTableHeaders.map((head) => t(head))}
            items={documentTableData}
            onSelect={setSelectedDocument}
            pageNumber={1}
            selectedItem={selectedDocument}
            widths={['40', '30', '30']}
          />
        </TableContainer>
      </TableWrapper>

      <Separator />
      <ButtonRow>
        <AsyncButton
          color={ButtonColor.Primary}
          disabled={isLoading}
          onClick={handleSubmit}
          size={ButtonSize.Medium}
          text={t('cardEntry_appDocumentUpload_issueCard_button')}
        />
      </ButtonRow>
    </Container>
  );
};

export default ApplicationDocumentModal;
